import React, { ReactElement } from 'react';
import classNames from '../../../utils/class-names';
import iconMap from '../../../utils/icon-map';
import { SidebarNavItem } from '../../../types';

interface SidebarGroupItemProps {
  item: SidebarNavItem;
  isCollapsed: boolean;
  isExpanded: boolean;
  toggleItem: (itemName: string) => void;
  handleMouseEnter: (e: React.MouseEvent<HTMLElement>, item: SidebarNavItem) => void;
  handleMouseLeave: () => void;
}

const SidebarGroupItem = ({
  item,
  isCollapsed,
  isExpanded,
  toggleItem,
  handleMouseEnter,
  handleMouseLeave,
}: SidebarGroupItemProps): ReactElement => {
  const renderSubItems = () => {
    if (!item.subItems || !isExpanded || isCollapsed) return null;

    return (
      <>
        {item.subItems.map((subItem) => (
          <div className="relative" key={subItem.id}>
            <div
              className={classNames(
                subItem.current ? 'bg-blue-500' : 'bg-slate-300',
                'absolute inset-y-0 left-5 w-0.5 z-10'
              )}
            ></div>
            <div className="relative z-0">
              <a
                id={subItem.id}
                key={subItem.name}
                href={subItem.href}
                className={classNames(
                  subItem.current
                    ? 'bg-slate-700 text-white font-medium'
                    : 'text-slate-300 hover:bg-slate-900 hover:text-slate-200',
                  'nav-item group flex items-center justify-between w-full px-2 py-2 text-sm font-normal rounded-md whitespace-nowrap pl-11'
                )}
              >
                <div className="flex items-center">
                  {subItem.icon && (
                    iconMap[subItem.icon] && (
                      React.createElement(iconMap[subItem.icon], {
                        className: classNames(
                          subItem.current
                            ? 'text-slate-300'
                            : 'text-slate-400 group-hover:text-slate-200',
                          'mr-3 flex-shrink-0 h-5 w-5'
                        ),
                        'aria-hidden': 'true',
                      })
                    )
                  )}
                  <span className="truncate">{subItem.name}</span>
                </div>
              </a>
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      <button
        key={item.name}
        onClick={() => toggleItem(String(item.name))}
        onMouseEnter={(e) => handleMouseEnter(e, item)}
        onMouseLeave={handleMouseLeave}
        className={classNames(
          item.current
            ? 'bg-slate-800 text-white'
            : 'text-slate-100 hover:bg-slate-800 hover:text-white',
          'group overflow-hidden flex items-center justify-between text-left w-full px-2 py-2 text-sm font-normal rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500'
        )}
      >
        <div className="flex items-center">
          {item.icon && (
            React.createElement(iconMap[item.icon], {
              className: classNames(
                item.current
                  ? 'text-slate-300'
                  : 'text-slate-400 group-hover:text-slate-200',
                'mr-3 flex-shrink-0 h-5 w-5'
              ),
              'aria-hidden': 'true',
            })
          )}
          <span
            className={classNames(
              isCollapsed ? 'opacity-0' : 'opacity-100',
              'transition-opacity duration-300'
            )}
          >
            {item.name}
          </span>
        </div>
        {!isCollapsed && item.subItems && (
          <div className={classNames('transform duration-150', isExpanded ? 'rotate-90' : '')}>
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        )}
      </button>
      {renderSubItems()}
    </>
  );
};

export default SidebarGroupItem;
